import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Save,
  ImageOff,
  Images,
} from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { PageHeader } from "@cms/ui/components/page-header";
import { MediaPickerDialog } from "../components/media-picker-dialog";
import {
  fetchGalleries,
  renameGallery,
  uploadGalleryImage,
  addMediaToGallery,
  updateGalleryImage,
  reorderGalleryImages,
  deleteGalleryImage,
  imageUrl,
  type Gallery,
  type GalleryImage,
} from "../lib/galleries";
import type { MediaItem } from "../lib/media";
import { moveItem } from "../lib/menus";
import type { ClientOutletContext } from "../lib/outlet-context";

// Per-image dirty tracking mirrors the menus page: compare against the
// last-saved snapshot keyed by image id.
function snapshotFields(images: GalleryImage[]): Record<string, string> {
  return Object.fromEntries(
    images.map((img) => [img.id, JSON.stringify({ caption: img.caption, alt: img.alt })])
  );
}

function ImageCard({ image, index, count, assetsBaseUrl, dirty, saving, selected, onToggleSelect, onField, onSave, onMove, onRemove }: {
  image: GalleryImage;
  index: number;
  count: number;
  assetsBaseUrl: string | null;
  dirty: boolean;
  saving: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onField: (fields: Partial<Pick<GalleryImage, "caption" | "alt">>) => void;
  onSave: () => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const thumbSrc = imageUrl(image, assetsBaseUrl, { thumb: true });

  return (
    <Card>
      <CardContent className="flex gap-4 p-4">
        <Checkbox
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select photo ${index + 1}`}
          className="mt-1 self-start"
        />
        <div className="h-28 w-36 shrink-0 overflow-hidden rounded-md bg-muted">
          {thumbSrc ? (
            <img src={thumbSrc} alt={image.alt || ""} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImageOff className="h-6 w-6" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <Label htmlFor={`caption-${image.id}`} className="text-xs">Caption</Label>
            <Input
              id={`caption-${image.id}`}
              value={image.caption}
              maxLength={500}
              placeholder="Shown under the photo (optional)"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onField({ caption: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`alt-${image.id}`} className="text-xs">Alt text</Label>
            <Input
              id={`alt-${image.id}`}
              value={image.alt}
              maxLength={300}
              placeholder="Describe the photo for screen readers and search engines"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onField({ alt: e.target.value })}
            />
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end justify-between">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={index === 0} aria-label="Move up" onClick={() => onMove(-1)}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={index === count - 1}
              aria-label="Move down"
              onClick={() => onMove(1)}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              aria-label="Delete image"
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button size="sm" disabled={!dirty || saving} onClick={onSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function GalleryDetailPage() {
  const { api, refreshStatus, setBreadcrumbs } = useOutletContext<ClientOutletContext>();
  const { id = "" } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [assetsBaseUrl, setAssetsBaseUrl] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [savedFields, setSavedFields] = useState<Record<string, string>>({});
  const [savingImage, setSavingImage] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GalleryImage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (!api) return;
    fetchGalleries(api)
      .then((data) => {
        const found = data.galleries.find((g) => g.id === id);
        if (!found) {
          setNotFound(true);
          return;
        }
        setGallery(found);
        setAssetsBaseUrl(data.assetsBaseUrl);
        setName(found.name);
        setSavedName(found.name);
        setSavedFields(snapshotFields(found.images));
      })
      .catch((err: Error) => setError(err.message));
  }, [api, id]);

  const setImages = (images: GalleryImage[]) =>
    setGallery((prev) => (prev ? { ...prev, images } : prev));

  const hasUnsavedChanges =
    !!gallery &&
    (name.trim() !== savedName ||
      gallery.images.some(
        (img) => JSON.stringify({ caption: img.caption, alt: img.alt }) !== savedFields[img.id]
      ));

  // Standard header breadcrumb: Galleries › <this gallery>, matching the Pages
  // and Menus editors. The Galleries crumb confirms before navigating away
  // with unsaved name/caption/alt edits.
  useEffect(() => {
    if (!setBreadcrumbs) return undefined;
    setBreadcrumbs(gallery ? [
      {
        label: "Galleries",
        href: "/media/galleries",
        onClick: (event: React.MouseEvent) => {
          if (hasUnsavedChanges && !window.confirm("You have unsaved changes. Leave anyway?")) {
            event.preventDefault();
          }
        },
      },
      { label: savedName || "Gallery" },
    ] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, gallery, savedName, hasUnsavedChanges]);

  const saveName = async () => {
    if (!api) return;
    setSavingName(true);
    setError("");
    try {
      await renameGallery(api, id, name.trim());
      setSavedName(name.trim());
      setName(name.trim());
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingName(false);
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (!api || !files.length) return;
    setUploading(true);
    setError("");
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : "Uploading…");
        const image = await uploadGalleryImage(api, id, files[i]);
        setGallery((prev) => (prev ? { ...prev, images: [...prev.images, image] } : prev));
        setSavedFields((prev) => ({
          ...prev,
          [image.id]: JSON.stringify({ caption: image.caption, alt: image.alt }),
        }));
      }
      refreshStatus?.();
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : err}`);
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // "Add from library": register existing media items against this gallery.
  const addFromLibrary = async (items: MediaItem[]) => {
    setPickerOpen(false);
    if (!api || !items.length) return;
    setUploading(true);
    setError("");
    try {
      for (const item of items) {
        const image = await addMediaToGallery(api, id, item);
        setGallery((prev) => (prev ? { ...prev, images: [...prev.images, image] } : prev));
        setSavedFields((prev) => ({
          ...prev,
          [image.id]: JSON.stringify({ caption: image.caption, alt: image.alt }),
        }));
      }
      refreshStatus?.();
    } catch (err) {
      setError(`Couldn't add from library: ${err instanceof Error ? err.message : err}`);
    } finally {
      setUploading(false);
    }
  };

  const saveImage = async (image: GalleryImage) => {
    if (!api || !gallery) return;
    setSavingImage(image.id);
    setError("");
    try {
      const fields = { caption: image.caption.trim(), alt: image.alt.trim() };
      await updateGalleryImage(api, id, image.id, fields);
      setImages(gallery.images.map((img) => (img.id === image.id ? { ...img, ...fields } : img)));
      setSavedFields((prev) => ({ ...prev, [image.id]: JSON.stringify(fields) }));
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingImage("");
    }
  };

  // Reorder persists immediately — optimistic update, rolled back on failure.
  const move = async (index: number, delta: -1 | 1) => {
    if (!api || !gallery) return;
    const next = moveItem(gallery.images, index, delta);
    if (next === gallery.images) return;
    const previous = gallery.images;
    setImages(next);
    setError("");
    try {
      await reorderGalleryImages(api, id, next.map((img) => img.id));
      refreshStatus?.();
    } catch (err) {
      setImages(previous);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeImage = async () => {
    if (!api || !gallery || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deleteGalleryImage(api, id, deleteTarget.id);
      setImages(gallery.images.filter((img) => img.id !== deleteTarget.id));
      setSelected((prev) => {
        if (!prev.has(deleteTarget.id)) return prev;
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      setDeleteTarget(null);
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelected = (imageId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });

  const allSelected = !!gallery && gallery.images.length > 0 && gallery.images.every((img) => selected.has(img.id));

  const toggleSelectAll = () => {
    if (!gallery) return;
    setSelected(allSelected ? new Set() : new Set(gallery.images.map((img) => img.id)));
  };

  // Bulk removal deletes one by one; anything already removed stays removed if
  // a later delete fails (the dialog then shows the remaining count).
  const removeSelected = async () => {
    if (!api || !gallery || selected.size === 0) return;
    setBulkDeleting(true);
    setError("");
    const removed = new Set<string>();
    try {
      for (const imageId of selected) {
        await deleteGalleryImage(api, id, imageId);
        removed.add(imageId);
      }
      setBulkDeleteOpen(false);
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (removed.size) {
        setGallery((prev) =>
          prev ? { ...prev, images: prev.images.filter((img) => !removed.has(img.id)) } : prev
        );
        setSelected((prev) => new Set([...prev].filter((imageId) => !removed.has(imageId))));
      }
      setBulkDeleting(false);
    }
  };

  if (notFound) {
    return (
      <div>
        <PageHeader title="Gallery not found" description="This gallery may have been deleted." />
        <Button asChild variant="outline">
          <Link to="/media/galleries">
            <ArrowLeft className="h-4 w-4" />
            Back to galleries
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={savedName || "Gallery"}
        description="Add photos, captions and alt text. Changes go live the next time you publish."
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!gallery && !error && <LoadingSpinner className="py-20" />}

      {gallery && (
        <div className="space-y-6">
          <Card>
            <CardContent className="flex items-end gap-3 p-4">
              <div className="max-w-sm flex-1 space-y-1.5">
                <Label htmlFor="gallery-name">Gallery name</Label>
                <Input
                  id="gallery-name"
                  value={name}
                  maxLength={100}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={savingName || !name.trim() || name.trim() === savedName}
                onClick={saveName}
              >
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Rename
              </Button>
              <div className="ml-auto flex items-center gap-3">
                {uploadProgress && <p className="text-sm text-muted-foreground">{uploadProgress}</p>}
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => setPickerOpen(true)}>
                  <Images className="h-4 w-4" />
                  Add from library
                </Button>
                <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Add photos
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.gif"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadFiles(Array.from(e.target.files ?? []))}
                />
              </div>
            </CardContent>
          </Card>

          {gallery.images.length === 0 && !uploading && (
            <Card>
              <CardContent className="py-14 text-center text-muted-foreground">
                No photos yet — click "Add photos" to upload. Photos are resized automatically so
                your site stays fast.
              </CardContent>
            </Card>
          )}

          {gallery.images.length > 0 && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all photos"
                />
                Select all
              </label>
              {selected.size > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove selected ({selected.size})
                </Button>
              )}
            </div>
          )}

          <div className="space-y-3">
            {gallery.images.map((image, index) => (
              <ImageCard
                key={image.id}
                image={image}
                index={index}
                count={gallery.images.length}
                assetsBaseUrl={assetsBaseUrl}
                dirty={JSON.stringify({ caption: image.caption, alt: image.alt }) !== savedFields[image.id]}
                saving={savingImage === image.id}
                selected={selected.has(image.id)}
                onToggleSelect={() => toggleSelected(image.id)}
                onField={(fields) =>
                  setImages(gallery.images.map((img) => (img.id === image.id ? { ...img, ...fields } : img)))
                }
                onSave={() => saveImage(gallery.images[index])}
                onMove={(delta) => move(index, delta)}
                onRemove={() => setDeleteTarget(image)}
              />
            ))}
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remove this photo?"
        description="The photo is removed from this gallery but stays in your media library, so you can reuse it elsewhere. It stays on your live site until you next publish."
        confirmLabel="Remove photo"
        busy={deleting}
        onConfirm={removeImage}
      />

      <ConfirmDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={(open) => !open && setBulkDeleteOpen(false)}
        title={`Remove ${selected.size} ${selected.size === 1 ? "photo" : "photos"}?`}
        description="The photos are removed from this gallery but stay in your media library, so you can reuse them elsewhere. They stay on your live site until you next publish."
        confirmLabel={selected.size === 1 ? "Remove photo" : `Remove ${selected.size} photos`}
        busy={bulkDeleting}
        onConfirm={removeSelected}
      />

      {/* Add-from-library picker (multi-select). */}
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        api={api}
        multiple
        onSelect={(items: MediaItem[]) => { void addFromLibrary(items); }}
      />
    </div>
  );
}
